import type { DocSection } from '../types'
import { FIG_DISPOSITION } from '../figures'

export const dispositionsSection: DocSection = {
  id: 'dispositions',
  title: "Qualifier un appel (dispositions)",
  icon: 'check-circle',
  accent: '#22C55E',
  summary: "Le résultat que vous posez en fin d'appel. C'est lui qui décide de la suite : rappeler, relancer, clôturer.",
  minRole: 'agent',
  articles: [
    {
      id: 'principe',
      title: "C'est quoi une disposition ?",
      summary: "Le résultat d'appel que vous choisissez à la fin, et pourquoi c'est important.",
      icon: 'flag',
      blocks: [
        { type: 'p', text: "Une **disposition** est le **résultat** que vous donnez à un appel quand il se termine (ex. « Intéressé », « Messagerie », « Vente »). Au raccrochage, la **fiche de clôture** s'ouvre dans le softphone : vous choisissez une disposition, éventuellement vous ajoutez des notes, puis vous validez." },
        { type: 'p', text: "Ce choix n'est pas qu'une étiquette : son **type** décide automatiquement **ce que devient le numéro** (rappel programmé, remise en file pour relance, ou clôture)." },
        { type: 'figure', svg: FIG_DISPOSITION, alt: 'Fiche de fin d\'appel avec les boutons de disposition', caption: "La fiche de clôture s'ouvre au raccrochage : un clic sur le résultat suffit." },
        { type: 'callout', variant: 'warning', title: 'Toujours qualifier', text: "Tant que vous n'avez pas qualifié l'appel, vous ne passez pas au suivant. C'est ce qui garantit qu'aucun appel ne reste « sans résultat » dans les statistiques." },
        { type: 'seealso', items: [
          { to: 'dispositions/types', label: 'Les types et leurs effets' },
          { to: 'dispositions/defaut', label: 'Les dispositions par défaut' },
          { to: 'rappels', label: 'Rappels & rendez-vous' },
        ] },
      ],
    },
    {
      id: 'types',
      title: 'Les types et leurs effets',
      summary: 'Les 5 familles de dispositions et ce qu\'elles déclenchent.',
      icon: 'git-branch',
      blocks: [
        { type: 'p', text: "Chaque disposition appartient à l'un de ces **5 types**. Le type pilote la suite :" },
        { type: 'table', headers: ['Type', 'Effet sur le numéro'], rows: [
          ['**Reprogrammer**', "Crée un **rappel programmé** : vous indiquez une date/heure, le numéro vous revient à ce moment-là."],
          ['**Réussi, résultat positif**', "**Clôture** l'appel comme une réussite (Intéressé, Vente, RDV). Compte comme conversion."],
          ['**Réussi, résultat négatif**', "**Clôture** : contact joint mais sans suite (Pas intéressé)."],
          ['**Échec, appelez plus tard**', "**Remet le numéro en file** pour une relance, **s'il reste des tentatives** (Pas de réponse, Messagerie). Le délai de relance de la campagne s'applique."],
          ['**Échec, ne pas rappeler**', "**Clôture définitive** : le numéro ne sera plus appelé (Faux numéro, Ne pas rappeler)."],
        ] },
        { type: 'callout', variant: 'info', title: 'Relance automatique', text: "Pour les types « Échec, appelez plus tard », la campagne définit un **nombre de tentatives** et un **délai entre tentatives**. Une fois les tentatives épuisées, le numéro est clôturé automatiquement." },
      ],
    },
    {
      id: 'defaut',
      title: 'Les dispositions par défaut',
      summary: 'Les 12 dispositions fournies à chaque campagne et leur type.',
      icon: 'list',
      blocks: [
        { type: 'p', text: "Chaque campagne reçoit ce jeu par défaut. Un responsable peut l'adapter dans **Paramètres de Disposition** (page campagne) : renommer, recolorer, ajouter — et **supprimer** (icône corbeille en bout de ligne ; seules les dispositions **système** sont verrouillées)." },
        { type: 'table', headers: ['Disposition', 'Type', 'Effet'], rows: [
          ['Pas de réponse', 'Échec, appelez plus tard', 'Relance si tentatives restantes'],
          ['Messagerie', 'Échec, appelez plus tard', 'Relance (tombé sur répondeur)'],
          ['Pas intéressé', 'Réussi, résultat négatif', 'Clôture'],
          ['Intéressé', 'Réussi, résultat positif', 'Clôture (positif)'],
          ['Vente', 'Réussi, résultat positif', "Clôture + **enregistrement conservé**"],
          ['RDV', 'Réussi, résultat positif', "Clôture + **enregistrement conservé** + formulaire RDV"],
          ['Recouvrement', 'Réussi, résultat positif', "Clôture + **enregistrement conservé** + envoyé au **suivi administrateur**"],
          ['Fidélisation', 'Réussi, résultat positif', "Clôture + **enregistrement conservé** + envoyé au **suivi administrateur**"],
          ['Suite à donner', 'Reprogrammer', 'Programme un rappel'],
          ['Faux numéro', 'Échec, ne pas rappeler', 'Clôture définitive'],
          ['Ne pas rappeler', 'Échec, ne pas rappeler', 'Clôture définitive'],
          ['Autre', 'Échec, appelez plus tard', 'Relance possible'],
        ] },
        { type: 'callout', variant: 'tip', title: 'Audio protégé', text: "Les dispositions **Vente**, **RDV**, **Recouvrement** et **Fidélisation** marquent l'enregistrement comme « à conserver » : il échappe à la purge automatique de l'historique. Un responsable peut activer cette protection sur d'autres dispositions." },
        { type: 'callout', variant: 'info', title: 'Recouvrement & Fidélisation : appels suivis', text: "Un appel qualifié **Recouvrement** ou **Fidélisation** apparaît — en plus de l'historique — sur la page **Suivi d'appels**, où un administrateur le passe en revue et le **valide**. Le réglage « Suivi » est ajustable sur n'importe quelle disposition dans Paramètres de Disposition." },
      ],
    },
    {
      id: 'systeme',
      title: 'Dispositions système (répondeur, etc.)',
      summary: 'Les dispositions posées automatiquement par le serveur.',
      icon: 'server',
      minRole: 'team_admin',
      blocks: [
        { type: 'p', text: "En plus des dispositions « manuelles », chaque campagne a des **dispositions système** (en lecture seule) qui reproduisent les résultats posés automatiquement en mode progressif : « Voicemail Message Left » (répondeur avec message), « Ring Out » (sonné sans réponse), « Line Busy » (occupé), « Number Not in Service » (numéro invalide), etc." },
        { type: 'callout', variant: 'info', title: 'Lien avec le mode progressif', text: "Quand une campagne est en **mode progressif**, le système détecte les répondeurs et pose **automatiquement** le résultat « Messagerie » avant de passer au numéro suivant — sans déranger d'agent. Voir [[dialer|Mode progressif]]." },
      ],
    },
  ],
}
